// src/Components/Posts/Posts.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import "./Post.css";
import BarLoading from "../Loading/BarLoading";
import PostCards from "../PostCards/PostCards";
import client from "../../api/client";

const PAGE_SIZE = 20;

function Posts() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState(
    searchParams.get("sort") || "newest"
  );

  // ➜ Read page from URL
  const page = Number(searchParams.get("page") || 1);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    async function fetchPosts() {
      try {
        const params = new URLSearchParams({
          sort: sortOrder === "oldest" ? "old" : "new",
          limit: PAGE_SIZE,
          page, // ➜ Send page to backend
        });

        if (categoryName) {
          params.append("category", categoryName.replace(/-/g, " "));
        }

        const res = await client.get(`/api/machines?${params.toString()}`);
        if (!mounted) return;

        let fetched = res.data?.machines || [];

        if (sortOrder === "priceLowHigh")
          fetched.sort((a, b) => a.price - b.price);
        else if (sortOrder === "priceHighLow")
          fetched.sort((a, b) => b.price - a.price);

        // ➜ Append or replace
        if (page === 1) setPosts(fetched);
        else setPosts((prev) => [...prev, ...fetched]);
      } catch (err) {
        console.error("Error fetching posts:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchPosts();
    return () => {
      mounted = false;
    };
  }, [categoryName, sortOrder, page]);

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSortOrder(newSort);

    // Reset to page 1 on sort change
    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("sort", newSort);

    // <-- FIXED: navigate to frontend category route (not /api/category)
    navigate(
      `/category/${encodeURIComponent(
        categoryName ? categoryName.replace(/\s+/g, "-") : "all"
      )}?${params.toString()}`
    );
  };

  // Compute slug
  const categorySlug = categoryName
    ? categoryName.replace(/\s+/g, "-")
    : "all";

  // ➜ Compute next page
  const nextPage = page + 1;

  function loadMore() {
    const params = new URLSearchParams();
    params.set("page", String(nextPage));
    params.set("sort", sortOrder);

    // <-- FIXED: navigate to frontend category route (not /api/category)
    navigate(`/category/${encodeURIComponent(categorySlug)}?${params}`);
  }

  return (
    <div className="postParentDiv">
      <div className="postsHeader">
        <div className="headerContent">
          <h1 className="mainTitle">
            {categoryName
              ? categoryName.replace(/-/g, " ")
              : "Industrial Machinery"}
          </h1>
          <p className="mainSubtitle">
            {categoryName
              ? `Explore top machines in ${categoryName.replace(/-/g, " ")}`
              : "Premium equipment for your business needs"}
          </p>
        </div>
        <div className="resultsInfo">
          <span className="resultsCount">{posts.length} machines available</span>
        </div>
      </div>

      <section className="productsSection">
        <div className="sectionHeader">
          <h2 className="sectionTitle">Available Machinery</h2>
          <div className="sortControls">
            <select
              className="sortSelect"
              value={sortOrder}
              onChange={handleSortChange}
            >
              <option value="newest">Newest First</option>
              <option value="priceLowHigh">Price: Low to High</option>
              <option value="priceHighLow">Price: High to Low</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>

        <div className="productsGridContainer">
          {loading && page === 1 ? (
            <div className="loadingContainer">
              <BarLoading />
            </div>
          ) : (
            <div className="productsGrid">
              {posts.map((product, i) => (
                <div className="product-card" key={product.id || i}>
                  <PostCards product={product} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Load More Button */}
        <div className="loadMoreSection">
          <button
            type="button"
            className="loadMoreBtn"
            onClick={loadMore}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner"></div> Loading...
              </>
            ) : (
              "Load More Machinery"
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

export default Posts;
